import {
  Button,
  Checkbox,
  Modal,
  ModalError,
  Tooltip,
} from '@clearcover/atlas';
import { AxiosError } from 'axios';
import classnames from 'classnames';
import React, { FC, useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  DriverType,
  ExcludedDriverType,
  VehicleType,
} from '@clearcover/core-app-types';
import {
  editDriverDetailsPermissionsPolicy,
  removeDriverPermissionsPolicy,
} from 'Admin/authPolicies';
import { ConflictingDraftModal } from 'Policies/components/ConflictingDraftModal';
import { draftEndpointModifier, PolicyContext } from 'Policies/utils';
import { useModalHashChange } from 'Shared/hooks';
import { useLazyApi } from 'Shared/services/Api';
import { DriverForm } from '../DriverForm';
import styles from './styles.module.css';
import { FormModalUnderWritingErrors } from 'Shared/components';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';

export const RemoveDriverButton: FC<{
  driver: DriverType | ExcludedDriverType;
  useStyle?: boolean;
  withBackground?: boolean;
  disableRemoveButton?: boolean;
  removeButtonTooltip?: string;
}> = ({
  driver,
  useStyle = false,
  withBackground = false,
  disableRemoveButton = false,
  removeButtonTooltip = '',
}) => {
  const { id: policyId, draftId } = useParams<{
    id: string;
    draftId: string;
  }>();
  const { id: driverId } = driver;

  const {
    refetchHandler,
    details: { draftType },
    flags: { renewalQuotesEnabled },
    resourceEffectiveGrants,
  } = useContext(PolicyContext);

  const history = useHistory();

  const [{ loading }, removeDriver] = useLazyApi<{ draftId: string }>({
    url: `/policies/${policyId}/drivers/${driverId}/remove${draftEndpointModifier(
      draftType,
      renewalQuotesEnabled
    )}`,
  });

  const [error, setError] = useState<AxiosError | undefined>();

  const {
    underwritingErrors,
    setUnderwritingErrors,
    hasUnderwritingErrors,
  } = useUnderwritingErrors();

  const [confirmation, setConfirmation] = useState({
    livesNotWithPolicyHolder: false,
    hasNotAccessToVehicles: false,
  });

  const submitDriverRemove = async (options?: {
    bypassUnderwriting?: boolean;
  }) => {
    const execConfig = options?.bypassUnderwriting
      ? { params: { bypassUnderwriting: true } }
      : undefined;

    return removeDriver(execConfig)
      .then(draft => {
        draftId === draft.data.draftId
          ? refetchHandler()
          : history.push(`/policies/${policyId}/draft/${draft.data.draftId}`);
      })
      .catch(err => {
        if (hasUnderwritingErrors(err)) {
          setUnderwritingErrors(err);
        } else {
          setError(err);
        }
      });
  };

  const renderErrorModal = () => {
    switch (true) {
      case !!error?.message:
        return <ModalError errorMessage={error?.message ?? ''} />;
      case !!underwritingErrors?.length:
        return (
          <FormModalUnderWritingErrors
            errors={underwritingErrors}
            grants={resourceEffectiveGrants}
            submitIgnoringUnderwriting={() =>
              submitDriverRemove({ bypassUnderwriting: true })
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      data-testid={'edit-driver-buttons'}
      className={classnames({
        [styles.container]: useStyle,
        [styles.background]: withBackground,
      })}
    >
      <Modal
        toggle={openRemoveDriverModal => (
          <ConflictingDraftModal
            onApprove={openRemoveDriverModal}
            renderToggle={openModal => (
              <Tooltip tooltip={removeButtonTooltip} position="top">
                <Button
                  data-testid={'remove-driver-button'}
                  variant={'danger-secondary'}
                  onClick={() => {
                    openModal();
                    setError(undefined);
                  }}
                  disabled={disableRemoveButton}
                >
                  Remove
                </Button>
              </Tooltip>
            )}
          ></ConflictingDraftModal>
        )}
      >
        {close => (
          <div className={styles.modal}>
            {renderErrorModal() || (
              <div data-testid={'remove-driver-modal'}>
                <h4>Confirm the following to continue</h4>
                <p>
                  Before removing the driver, confirm that the following
                  criteria are met:
                </p>
                <Checkbox
                  label="This driver does not live in the household."
                  name="livesNotWithPolicyHolder"
                  value={confirmation.livesNotWithPolicyHolder}
                  onChange={value =>
                    setConfirmation(current => ({
                      ...current,
                      livesNotWithPolicyHolder: value,
                    }))
                  }
                />
                <Checkbox
                  label="This driver does not have regular access to an insured vehicle."
                  name="hasNotAccessToVehicles"
                  value={confirmation.hasNotAccessToVehicles}
                  onChange={value =>
                    setConfirmation(current => ({
                      ...current,
                      hasNotAccessToVehicles: value,
                    }))
                  }
                />
                <div className={styles.buttonGroup}>
                  <Button
                    disabled={loading}
                    data-testid={'remove-driver-cancel'}
                    onClick={close}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => submitDriverRemove()}
                    isSubmitting={loading}
                    data-testid={'remove-vehicle-confirm'}
                    variant="primary"
                    disabled={
                      !confirmation.livesNotWithPolicyHolder ||
                      !confirmation.hasNotAccessToVehicles
                    }
                  >
                    Remove Driver
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export const EditDriverButtons: FC<{
  driver: DriverType;
  vehicles: Array<VehicleType>;
  withBackground?: boolean;
  disableRemoveButton?: boolean;
  removeButtonTooltip?: string;
}> = ({
  driver,
  vehicles,
  withBackground = true,
  disableRemoveButton = false,
  removeButtonTooltip = '',
}) => {
  const { modalHashChange } = useModalHashChange('editDriver');
  const { resourceEffectiveGrants } = useContext(PolicyContext);

  const canRemoveDriver = removeDriverPermissionsPolicy(
    resourceEffectiveGrants
  );

  const canEditDriver = editDriverDetailsPermissionsPolicy(
    resourceEffectiveGrants
  );

  return (
    <div
      data-testid={'edit-driver-buttons'}
      className={classnames(styles.container, {
        [styles.background]: withBackground,
      })}
    >
      {canRemoveDriver && (
        <RemoveDriverButton
          driver={driver}
          withBackground={false}
          disableRemoveButton={disableRemoveButton}
          removeButtonTooltip={removeButtonTooltip}
        />
      )}

      {canEditDriver && (
        <Modal
          onVisibilityChange={modalHashChange}
          variant={'full'}
          toggle={openEditDriverModal => (
            <ConflictingDraftModal
              onApprove={openEditDriverModal}
              renderToggle={openModal => (
                <Button
                  className={styles.editDriverButton}
                  size="sm"
                  onClick={event => {
                    event.stopPropagation();
                    openModal();
                  }}
                  variant="secondary"
                  data-testid={`${driver.id}-edit-driver-button`}
                >
                  Edit
                </Button>
              )}
            />
          )}
        >
          {close => (
            <DriverForm
              type="update"
              driver={driver}
              vehicles={vehicles}
              close={close}
            />
          )}
        </Modal>
      )}
    </div>
  );
};
